import {
  Component,
  forwardRef,
  OnInit,
  OnDestroy,
  AfterViewInit,
  EventEmitter,
  Output,
  Input,
  ViewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { ThemeLayoutService } from 'src/app/@theme/services/theme-layout.service';
import { BaseService } from 'src/app/tabms/services/base/base.service';
import { FieldDicHelperService } from 'src/app/tabms/services/global/field-dic-helper.service';
import { ActivatedRoute } from '@angular/router';
import { RTL } from 'src/app/@theme/const';
import { TabConst } from 'src/app/tabms/tab-consts';
import { Calendar } from 'primeng/calendar';

@Component({
  selector: 'app-p-time-picker',
  templateUrl: './p-time-picker.component.html',
  styleUrl: './p-time-picker.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PTimePickerComponent),
      multi: true,
    },
  ],
})
export class PTimePickerComponent implements OnInit, OnDestroy, AfterViewInit, ControlValueAccessor {

  @Output() timeSelected = new EventEmitter<string>();

  @Input() fieldName: string;
  @Input() validate: boolean = true;

  selectedTime: Date | null = null;
  placeholder: string;
  currentLang: string;
  systemMenuId: string;
  @Input() size: 'default' | 'compact' | 'xs' = TabConst.DEFAULTSIZE;

  @ViewChild('timeCalendar') timeCalendar: Calendar;

  private destroy$ = new Subject<void>();
  private onChange = (value: any) => { };
  private onTouched = () => { };

  constructor(
    private baseService: BaseService,
    private themeLayoutService: ThemeLayoutService,
    private fieldDicHelperService: FieldDicHelperService,
    private activateRoute: ActivatedRoute
  ) {
    this.activateRoute.params.pipe(takeUntil(this.destroy$)).subscribe({
      next: (params) => {
        this.systemMenuId = params['id'];
      },
    });

    this.currentLang = this.baseService.getCurrentLanguage();

    this.themeLayoutService.directionChange.pipe(takeUntil(this.destroy$)).subscribe({
      next: (dir) => {
        this.currentLang = dir === RTL ? 'ar' : 'en';
        this.loadFieldData();
      },
    });
  }

  ngOnInit(): void {
    this.loadFieldData();

    this.baseService.createFormDisplay
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res?.show && res?.id === null) {
            this.loadFieldData();
          }
        },
      });
  }

  ngAfterViewInit(): void { }

  onTimeSelect(): void {
    if (this.selectedTime) {
      // const timeString = this.selectedTime.toTimeString().split(' ')[0]; // HH:mm:ss
      const hours = this.selectedTime.getHours().toString().padStart(2, '0');
      const minutes = this.selectedTime.getMinutes().toString().padStart(2, '0');
      const seconds = '00';
      const timeString = `${hours}:${minutes}:${seconds}`;
      this.timeSelected.emit(timeString);
      this.onChange(timeString);
      this.onTouched();
    } else {
      this.onChange(null);
      this.onTouched();
    }
  }

  onTimeInput(event: any): void {
    // Handle direct input in the time field
    const inputValue = event.target.value;
    
    // If input is empty or cleared, reset the time
    if (!inputValue || inputValue.trim() === '') {
      this.selectedTime = null;
      this.timeSelected.emit(null);
      this.onChange(null);
      this.onTouched();
      return;
    }
    
    // If input has valid time format, update the time
    if (this.isValidTimeFormat(inputValue)) {
      const [hours, minutes] = inputValue.split(':').map(v => parseInt(v, 10));
      const date = new Date();
      date.setHours(hours || 0, minutes || 0, 0, 0);
      this.selectedTime = date;
      this.onTimeSelect();
    }
  }

  onTimeClear(): void {
    // Handle clear button click
    this.selectedTime = null;
    this.timeSelected.emit(null);
    this.onChange(null);
    this.onTouched();
  }

  reset(): void {
    // Public method to reset the time picker
    this.selectedTime = null;
    this.timeSelected.emit(null);
    this.onChange(null);
    this.onTouched();
    
    // Clear the input field display
    if (this.timeCalendar?.inputfieldViewChild?.nativeElement) {
      this.timeCalendar.inputfieldViewChild.nativeElement.value = '';
    }
  }

  onTimeBlur(event: any): void {
    // Handle when user leaves the input field
    const inputValue = event.target.value;
    
    // If input is empty after blur, ensure it's properly cleared
    if (!inputValue || inputValue.trim() === '') {
      this.selectedTime = null;
      this.timeSelected.emit(null);
      this.onChange(null);
      this.onTouched();
    }
  }

  private isValidTimeFormat(timeString: string): boolean {
    // Check if the time string matches HH:mm format
    const timeRegex = /^([0-1]?[0-9]|2[0-3]):([0-5]?[0-9])$/;
    return timeRegex.test(timeString);
  }

  // ControlValueAccessor methods
  writeValue(value: any): void {
    if (!value || value === null || value === '') {
      this.selectedTime = null;
      return;
    }

    if (typeof value === 'string') {
      // Assume it's "HH:mm:ss"
      const [hours, minutes, seconds] = value.split(':').map((v) => parseInt(v, 10));
      const date = new Date();
      date.setHours(hours || 0, minutes || 0, seconds || 0, 0);
      this.selectedTime = date;
    } else if (value instanceof Date) {
      this.selectedTime = value;
    } else if (typeof value === 'object' && 'hours' in value && 'minutes' in value && 'seconds' in value) {
      // Handle TimeSpan-like object
      const date = new Date();
      date.setHours(value.hours || 0, value.minutes || 0, value.seconds || 0, 0);
      this.selectedTime = date;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.timeCalendar?.inputfieldViewChild?.nativeElement?.setAttribute('disabled', 'true');
    } else {
      this.timeCalendar?.inputfieldViewChild?.nativeElement?.removeAttribute('disabled');
    }
  }

  private loadFieldData() {
    this.fieldDicHelperService
      .getFieldData(this.fieldName, this.systemMenuId, this.validate, null, this.currentLang)
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ placeholder, defaultValue, type, disabled }) => {
        this.placeholder = placeholder;
        if (defaultValue !== null && defaultValue !== undefined) {
          this.writeValue(defaultValue);
          this.onChange(defaultValue);
        }

        if (disabled) {
          this.setDisabledState(true);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}